<template>
    <gh-modal ref="modalRef">
        <gh-loading :visible="loadingStore.showLoading" fullscreen></gh-loading>
        <template #title>
            <span class="gh-modal-title">
                <span class="newicon-sharework"></span>
                發佈分享
            </span>
        </template>

        <div class="portfolio-share-body">
            <p class="portfolio-share-tip">
                <template v-if="shareStatus === 'NotSharing'">
                    您可以分享作品給好友
                    <br />
                    或同時分享至「
                    <a href="/Portfolio/Shared">作品分享區</a>
                    」！
                </template>
                <template v-else>您已經分享過了，是否要重新製作？</template>
            </p>

            <template v-if="shareStatus === 'NotSharing'">
                <p class="portfolio-share-note">
                    按下分享作品按鈕，立即分享您的作品！
                </p>
                <label class="portfolio-share-checkbox">
                    <input v-model="shareToGainHow" type="checkbox" />
                    我想要同時分享到「作品分享區」
                </label>
            </template>
        </div>

        <template #footer>
            <div class="portfolio-share-actions">
                <template v-if="shareStatus === 'NotSharing'">
                    <gh-button
                        :variant="ButtonVariant.Primary"
                        @click="onShare"
                    >
                        分享作品
                    </gh-button>
                    <gh-button :variant="ButtonVariant.White" @click="onClose">
                        不要分享
                    </gh-button>
                </template>

                <template v-else>
                    <gh-button
                        :variant="ButtonVariant.Primary"
                        @click="onBrowse"
                    >
                        直接瀏覽作品
                    </gh-button>
                    <gh-button
                        :variant="ButtonVariant.Primary"
                        @click="onRemake"
                    >
                        重新製作作品
                    </gh-button>
                    <gh-button
                        :variant="ButtonVariant.White"
                        @click="onCancelShare"
                    >
                        取消公開分享
                    </gh-button>
                </template>
            </div>
        </template>
    </gh-modal>
</template>

<script setup lang="ts">
    import { ref } from 'vue';
    import {
        getPortfolioShareStatus,
        submitPortfolioShare,
    } from '@/api/portfolio/portfolio.api';
    import GhButton from '@/components/base/GhButton.vue';
    import GhLoading from '@/components/base/GhLoading.vue';
    import { useAppLoadingStore } from '@/stores/loading.store';
    import {
        ButtonVariant,
        ModalSize,
    } from '@/types/components/base-components.types';

    type ShareStatus = '' | 'Shared' | 'NotSharing';

    interface ModalController {
        open: (options?: Record<string, unknown>) => void;
        close: () => void;
    }

    interface ShareStatusResponse {
        status?: string;
    }

    // ----- state -----
    const loadingStore = useAppLoadingStore();
    const modalRef = ref<ModalController | null>(null);
    const bookId = ref('');
    const shareStatus = ref<ShareStatus>('');
    const shareToGainHow = ref(false);

    // ----- methods -----
    // 先查詢作品分享狀態，未完成的作品只提示不開啟 modal
    const openForBook = (id: string): void => {
        getPortfolioShareStatus<ShareStatusResponse>(id)
            .then((response) => {
                const status = response.data?.status;

                if (status === 'Shared' || status === 'NotSharing') {
                    bookId.value = id;
                    shareStatus.value = status;
                    shareToGainHow.value = false;
                    modalRef.value?.open({
                        showReject: false,
                        closeOnOverlay: false,
                        closeOnEsc: false,
                        size: ModalSize.Md,
                    });

                    return;
                }

                window.alert(
                    '您的作品尚未全部完成，請您完成「未置入圖片」的頁面，再進行分享！'
                );
            })
            .catch(() => {
                window.alert('無法分享作品，請稍候再試');
            });
    };

    // 送出分享決定後，取消公開只關閉 modal，其餘導向線上編輯器的分享流程
    const doShare = (mode: string): void => {
        const isResharing = mode !== '直接瀏覽作品';
        const cancelSharing = mode === 'Cancel';
        const param =
            '&shareToGainHow=' +
            shareToGainHow.value +
            '&isResharing=' +
            isResharing +
            '&cancelSharing=' +
            cancelSharing;

        submitPortfolioShare({
            bookId: bookId.value,
            shareToGainHow: shareToGainHow.value,
            isResharing,
            cancelSharing,
        }).then(() => {
            if (cancelSharing) {
                modalRef.value?.close();

                return;
            }

            window.location.href =
                '/Editor/OnlineEditor/DoShare?bookID=' + bookId.value + param;
        });
    };

    const onShare = (): void => doShare('分享作品');
    const onClose = (): void => modalRef.value?.close();
    const onBrowse = (): void => doShare('直接瀏覽作品');
    const onRemake = (): void => doShare('重新製作作品');
    const onCancelShare = (): void => doShare('Cancel');

    // ----- expose -----
    defineExpose({
        openForBook,
    });
</script>

<style scoped lang="scss">
    .portfolio-share-tip {
        color: $textColor45;
        font-size: 16px;
        font-weight: 700;
        line-height: 1.5;
    }

    .portfolio-share-note {
        margin-top: 16px;
    }

    .portfolio-share-checkbox {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        margin-top: 8px;
        color: $textColor888;
        cursor: pointer;

        input {
            display: block;
        }
    }

    .portfolio-share-actions {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 8px;

        :deep(.btn) {
            padding: 4px 24px;
        }
    }
</style>
